import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCorpId } from '../utils/api';
import * as XLSX from 'xlsx';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'active' ? 'badge badge-success' : s === 'inactive' ? 'badge badge-danger' : s === 'pending' ? 'badge badge-warning' : 'badge badge-neutral';
  return <span className={cls}>{status}</span>;
};

function BillingTable({ onEdit }) {
  const navigate = useNavigate();
  const [billings, setBillings] = useState([]);
  const [filteredBillings, setFilteredBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ employeeName: '', vendorName: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const menuRef = useRef();
  const statusOptions = useMemo(() => {
    const set = new Set(); (billings || []).forEach(b => { if (b?.status) set.add(b.status); });
    return Array.from(set).sort();
  }, [billings]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchBillings = async () => {
    try {
      const data = await fetchWithCorpId(`/billing?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setBillings(data); setFilteredBillings(data); setTotalElements(data.length); setTotalPages(1);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setBillings(content); setFilteredBillings(content);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
      }
    } catch { setError('Failed to load billings'); setBillings([]); setFilteredBillings([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchBillings();
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) {} };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [page, size]);
  useEffect(() => { applyFilters(); }, [filter, billings]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const applyFilters = () => {
    if (!Array.isArray(billings)) { setFilteredBillings([]); return; }
    setFilteredBillings(billings.filter(b =>
      (!filter.employeeName || b.employeeName?.toLowerCase().includes(filter.employeeName.toLowerCase())) &&
      (!filter.vendorName || b.vendorName?.toLowerCase().includes(filter.vendorName.toLowerCase())) &&
      (!filter.status || b.status?.toLowerCase() === filter.status.toLowerCase())
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key || !Array.isArray(filteredBillings)) return;
    setFilteredBillings(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredBillings.map(b => ({
      'Billing ID': b.id, Employee: b.employeeName, Vendor: b.vendorName,
      'Billing Code': b.billingCode, Rate: b.billRate, Frequency: b.invoiceFrequency,
      'Due Days': b.invoiceDueDays, Description: b.itemDescription,
      'Start Date': b.startDate, 'First Period End': b.firstPeriodEndDate,
      'End Date': b.endDate, Status: b.status,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Billings');
    XLSX.writeFile(wb, 'billings.xlsx');
  };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Billings</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Manage billing rates and schedules</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-info" onClick={handleDownloadExcel}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button className="btn-primary" onClick={() => navigate('/on/billings/new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Billing
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <input name="employeeName" placeholder="Employee name" value={filter.employeeName} onChange={handleFilterChange} />
        <input name="vendorName" placeholder="Vendor name" value={filter.vendorName} onChange={handleFilterChange} />
        <select name="status" value={filter.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn-secondary" onClick={() => setFilter({ employeeName: '', vendorName: '', status: '' })}>Clear</button>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading billings…</div>
      ) : error ? (
        <div className="alert error">{error}</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')}>ID <SortIcon col="id" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('employeeName')}>Employee <SortIcon col="employeeName" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('vendorName')}>Vendor <SortIcon col="vendorName" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('billingCode')}>Code <SortIcon col="billingCode" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('billRate')}>Rate <SortIcon col="billRate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('invoiceFrequency')}>Frequency <SortIcon col="invoiceFrequency" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('invoiceDueDays')}>Due Days <SortIcon col="invoiceDueDays" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('itemDescription')}>Description <SortIcon col="itemDescription" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('startDate')}>Start <SortIcon col="startDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('firstPeriodEndDate')}>First Period End <SortIcon col="firstPeriodEndDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('endDate')}>End <SortIcon col="endDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status <SortIcon col="status" sortConfig={sortConfig} /></th>
              </tr>
            </thead>
            <tbody>
              {filteredBillings.length === 0 ? (
                <tr className="state-row"><td colSpan="12">No billing records found.</td></tr>
              ) : filteredBillings.map(b => (
                <tr key={b.id}>
                  <td><span className="table-link" onClick={() => onEdit(b)}>{b.id}</span></td>
                  <td style={{ fontWeight: '500' }}>{b.employeeName}</td>
                  <td style={{ color: '#64748b' }}>{b.vendorName}</td>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 7px', borderRadius: '5px', fontSize: '12.5px' }}>{b.billingCode}</code></td>
                  <td style={{ fontWeight: '600', color: '#1e293b' }}>{b.billRate}</td>
                  <td>{b.invoiceFrequency}</td>
                  <td style={{ textAlign: 'center' }}>{b.invoiceDueDays}</td>
                  <td style={{ color: '#64748b', fontSize: '13px', maxWidth: '200px' }}>{b.itemDescription}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{b.startDate}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{b.firstPeriodEndDate}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{b.endDate}</td>
                  <td><StatusBadge status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BillingTable;
